/**
 * JobDetailsBar
 * One-line job status indicator for prompt forms. Renders left of the Create/Retry button.
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import type { JobDetailsSummary, JobDetailsStatus } from '../types';
import { usePromptTranslations, type PromptTranslations } from '../i18n';

export type { JobDetailsSummary };

export interface JobDetailsBarProps {
    jobStatus: JobDetailsSummary | null;
    onDismiss?: () => void;
    className?: string;
}

const formatDuration = (seconds: number): string => {
    if (seconds < 1) return '<1s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
};

// EAA-2031: translated status labels.
const statusLabels = (t: PromptTranslations): Record<JobDetailsStatus, string> => ({
    pending: t.jobPending,
    running: t.jobRunning,
    completed: t.jobCompleted,
    failed: t.jobFailed,
    cancelled: t.jobCancelled,
});

export const JobDetailsBar: React.FC<JobDetailsBarProps> = ({
    jobStatus,
    onDismiss,
    className,
}) => {
    const t = usePromptTranslations();
    if (!jobStatus) return null;

    const { status, error, progress, duration_seconds } = jobStatus;
    const isTerminal = status === 'completed' || status === 'failed' || status === 'cancelled';

    // EAA-1674: raw text-green-600 → text-success semantic token so status
    // colors follow global design tokens instead of a hardcoded palette class.
    const statusColor = status === 'failed' ? 'text-destructive' : status === 'completed' ? 'text-success' : 'text-muted-foreground';

    return (
        <div
            className={cn('job-details-bar flex items-center gap-2 text-sm min-w-0 flex-1', className)}
        >
            {status === 'running' && (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent shrink-0" />
            )}
            {status === 'completed' && (
                <span className="text-success shrink-0" aria-hidden>✓</span>
            )}
            {status === 'failed' && (
                <span className="text-destructive shrink-0" aria-hidden>✗</span>
            )}
            {status === 'cancelled' && (
                <span className="text-muted-foreground shrink-0" aria-hidden>⊘</span>
            )}

            <span className={cn('font-medium truncate', statusColor)}>
                {statusLabels(t)[status]}
            </span>
            {error && status === 'failed' && (
                <span className="text-destructive truncate max-w-[140px]" title={error}>
                    · {error.length > 25 ? `${error.slice(0, 22)}…` : error}
                </span>
            )}
            {progress && !isTerminal && (
                <span className="text-muted-foreground text-xs tabular-nums shrink-0">
                    {/* EAA-2031: steps are pipeline stages, not questions — "(0/2)" beside a
                        5-question request read as the wrong count, so only the percent shows. */}
                    {Math.round(progress.percentage)}%
                </span>
            )}
            {isTerminal && duration_seconds !== undefined && duration_seconds > 0 && (
                <span className="text-muted-foreground text-xs shrink-0">
                    {formatDuration(duration_seconds)}
                </span>
            )}
            {isTerminal && onDismiss && (
                <button
                    type="button"
                    onClick={onDismiss}
                    className={cn(
                        'text-xs px-2 py-1 rounded border-none font-medium cursor-pointer shrink-0',
                        status === 'completed'
                            ? 'bg-success text-success-foreground hover:bg-success/90'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    )}
                >
                    {status === 'completed' ? t.jobContinue : t.jobDismiss}
                </button>
            )}
        </div>
    );
};

export default JobDetailsBar;
